import { FC } from 'react'

import { Chip, Box, Skeleton } from '@mui/material'

import { useGetTagListQuery } from '../../../../../../api/core'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { UnifiedNewsEntry } from '../../../../types/UnifiedNewsEntry'
import './ReportSubHeader.scss'

// Props interface for the ReportSubHeader component
type ReportSubHeaderProps = {
  newsEntry: UnifiedNewsEntry
}

/**
 * ReportSubHeader Component
 *
 * Displays tags associated with a report news entry. This component:
 * - Fetches tag metadata from the API based on user language
 * - Shows loading skeletons while tags are being fetched
 * - Renders tags as chips with proper styling
 * - Handles cases where no tags are present
 *
 * The component is designed to be used as a sub-header within news cards
 * to provide quick visual categorization of report content.
 */
export const ReportSubHeader: FC<ReportSubHeaderProps> = ({ newsEntry }) => {
  // Get current user's language for localized tag names
  const userLanguage = useCurrentUserLanguage()
  // Fetch tag list data from API with loading state
  const { data: reportTags, isLoading: reportTagsIsLoading } = useGetTagListQuery({ type: 'report', userLanguage })

  // Extract tags from the news entry, defaulting to empty array if none exist
  const tags = newsEntry.feedEntry.entryData.tags || []

  // Loading State: Show skeleton placeholders while fetching tag data
  if (reportTagsIsLoading) {
    return (
      <Box className="ReportSubHeader">
        <Box className="ReportSubHeader__tags">
          {/* Render 3 skeleton chips to simulate loading state */}
          {[1, 2, 3].map((index) => (
            <Skeleton key={index} variant="rectangular" width={80} height={24} sx={{ borderRadius: '16px', marginRight: 1, marginBottom: 1 }} />
          ))}
        </Box>
      </Box>
    )
  }

  // Early return if no tags are present - don't render anything
  if (!tags || tags.length === 0) {
    return null
  }

  // Main render: Display tags as styled chips
  return (
    <Box className="ReportSubHeader">
      <Box className="ReportSubHeader__tags">
        {tags.map((tag: string) => (
          <Chip
            key={tag}
            // Use localized tag name from API data, fallback to raw tag ID if not found
            label={reportTags?.find((t) => t.id === tag)?.name || tag}
            className="chip Chip--primary-light"
            sx={{
              color: 'black',
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
